"use client";
import { useState, useEffect, useRef } from "react";
import PlatformPageTemplate from "../../components/PlatformPageTemplate";
import FeatureScreenshot from "../../components/FeatureScreenshot";
import { useScrollAnimate } from "../../lib/hooks";
import { pageDate } from "../../lib/page-dates";

const MK_BG = "#0f0f11";
const MK_BD = "rgba(255,255,255,0.06)";
const MK_TEXT = "#a0a0a0";
const TEAL = "#4FCDC4";


// Marketing mockup: /t.js URL is illustrative (app uses inline snippet).
// Intentional - do not "fix" in audits. Kevin's decision 2026-04-21.
function CodeSnippetMockup() {
  const [ref, animated] = useScrollAnimate();
  const [charCount, setCharCount] = useState(0);
  const animRef = useRef(false);

  // Plain text for typing, with color spans applied after
  const lines = [
    '<script src="https://app.ai-advisors.ai/t.js"',
    '    data-key="YOUR_SNIPPET_KEY">',
    '</script>',
  ];
  const fullText = lines.join("\n");
  const totalChars = fullText.length;
  const typingSpeed = 30; // ms per character

  useEffect(() => {
    if (!animated || animRef.current) return;
    animRef.current = true;
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setCharCount(i);
      if (i >= totalChars) clearInterval(interval);
    }, typingSpeed);
    return () => clearInterval(interval);
  }, [animated, totalChars]);

  // Map typed characters back to colored spans per line
  const typed = fullText.slice(0, charCount);
  const typedLines = typed.split("\n");

  const colorize = (text, lineIdx) => {
    if (lineIdx === 0) {
      // <script src="https://app.ai-advisors.ai/t.js"
      const parts = [];
      const tag = text.slice(0, Math.min(text.length, 7)); // <script
      if (tag) parts.push(<span key="tag" style={{ color: "#a78bfa" }}>{tag}</span>);
      const rest = text.slice(7);
      if (rest) {
        const srcIdx = rest.indexOf('src=');
        if (srcIdx >= 0) {
          parts.push(<span key="sp1" style={{ color: "#a78bfa" }}>{rest.slice(0, srcIdx)}</span>);
          const afterSrc = rest.slice(srcIdx);
          const eqIdx = afterSrc.indexOf('=');
          parts.push(<span key="src" style={{ color: TEAL }}>{afterSrc.slice(0, eqIdx + 1)}</span>);
          const valPart = afterSrc.slice(eqIdx + 1);
          parts.push(<span key="val" style={{ color: "#f59e0b" }}>{valPart}</span>);
        } else {
          parts.push(<span key="rest" style={{ color: "#a78bfa" }}>{rest}</span>);
        }
      }
      return parts;
    }
    if (lineIdx === 1) {
      // data-key="YOUR_SNIPPET_KEY">
      const dkIdx = text.indexOf('data-key');
      if (dkIdx < 0) return <span style={{ color: TEAL }}>{text}</span>;
      const pre = text.slice(0, dkIdx);
      const afterDk = text.slice(dkIdx);
      const eqIdx = afterDk.indexOf('=');
      if (eqIdx < 0) return <><span style={{ color: TEAL }}>{pre}{afterDk}</span></>;
      const key = afterDk.slice(0, eqIdx + 1);
      const valRest = afterDk.slice(eqIdx + 1);
      const gtIdx = valRest.indexOf('>');
      const val = gtIdx >= 0 ? valRest.slice(0, gtIdx) : valRest;
      const gt = gtIdx >= 0 ? valRest.slice(gtIdx) : "";
      return (
        <>
          {pre && <span style={{ color: TEAL }}>{pre}</span>}
          <span style={{ color: TEAL }}>{key}</span>
          <span style={{ color: "#f59e0b" }}>{val}</span>
          {gt && <span style={{ color: "#a78bfa" }}>{gt}</span>}
        </>
      );
    }
    // </script>
    return <span style={{ color: "#a78bfa" }}>{text}</span>;
  };

  const showCursor = charCount < totalChars && animated;

  return (
    <div ref={ref} style={{ background: MK_BG, borderRadius: 12, border: "1px solid rgba(255,255,255,0.04)", overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${MK_BD}`, display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
        <span style={{ fontSize: 11, color: MK_TEXT, marginLeft: 8 }}>tracking-snippet.html</span>
      </div>
      <div style={{ padding: "20px 16px", fontFamily: "var(--mono)", fontSize: 13, lineHeight: 1.8, minHeight: 78 }}>
        {typedLines.map((line, i) => (
          <div key={i} style={{ display: "flex" }}>
            <span style={{ color: "rgba(255,255,255,0.2)", width: 28, flexShrink: 0, textAlign: "right", marginRight: 16, userSelect: "none" }}>{i + 1}</span>
            <span style={i === 1 ? { paddingLeft: 24 } : undefined}>
              {colorize(line, i)}
              {showCursor && i === typedLines.length - 1 && (
                <span style={{ display: "inline-block", width: 2, height: 14, background: TEAL, marginLeft: 1, verticalAlign: "middle", animation: "cursorBlink 0.8s step-end infinite" }} />
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
export default function AIAnalyticsContent() {
  return (
    <PlatformPageTemplate
      route="/platform/ai-analytics"
      fiveAStage={{ num: 1, action: "Track", anchor: "analytics" }}
      heroTitle="AI Analytics"
      heroSubtitle="The AI layer over the analytics you already run. See AI traffic beside every other channel, split paid from organic, and record the AI crawlers a browser snippet structurally cannot see."
      lastUpdated={pageDate("/platform/ai-analytics")}
      overviewCards={[
        { title: "18 AI Crawlers", description: "Identified by published user agent - GPTBot, ClaudeBot, OAI-SearchBot, Claude-SearchBot, PerplexityBot, Google-Extended and the rest" },
        { title: "4 Ways To Collect", description: "Cloudflare Worker, Next.js middleware, WordPress plugin, or the JavaScript snippet. The first three see crawlers; the snippet sees people" },
        { title: "IP-Verified", description: "Crawler identity checked against the operator's own published address ranges, so a claimed GPTBot has to prove it" },
      ]}
      flagshipScreenshot={[
        {
          src: "/screenshots/ai-analytics/01-overview.png",
          alt: "The AI Analytics Overview: AI referral visits, AI conversions and AI visibility above a referral trend chart.",
        },
        {
          src: "/screenshots/ai-analytics/03-traffic-by-channel.png",
          alt: "The Traffic tab comparing AI against every other channel in Google Analytics sessions, with AI Assistant carved out as its own row.",
        },
        {
          src: "/screenshots/ai-analytics/05-crawl-coverage.png",
          alt: "Crawl Coverage: AI bot visits by operator with a note naming which collection method recorded them and how many were IP-verified.",
        },
        {
          src: "/screenshots/ai-analytics/04-paid-vs-organic.png",
          alt: "AI traffic split into organic AI and paid ChatGPT Ads visits, with a referral trend and a per-assistant breakdown.",
        },
        {
          src: "/screenshots/ai-analytics/02-analytics-advisor.png",
          alt: "The Analytics Advisor panel with a written read of what changed this week across crawls, search and AI traffic, plus focus areas to act on.",
        },
      ]}
      features={[
        {
          title: "AI is a channel now. Measure it like one.",
          description: "Your Google Analytics channel report buries AI arrivals inside Referral and Direct. AI Analytics carves them out and puts them on the same table as Organic Search, Paid, Direct and Social, with sessions, week-over-week movement, engagement and share. You get the one thing a channel report is for: whether this is growing fast enough to plan around, measured the same way you measure everything else.",
          mockup: <FeatureScreenshot src="/screenshots/ai-analytics/03-traffic-by-channel.png" alt="The Traffic tab comparing AI against every other channel in Google Analytics sessions, with AI Assistant carved out as its own row alongside Direct, Referral and Organic Search." priority />,
        },
        {
          title: "Record the crawlers a browser cannot see",
          description: "AI crawlers do not run JavaScript. That is not a tuning problem, it is how they work, and it means a browser tracking snippet can never record GPTBot, ClaudeBot, OAI-SearchBot or PerplexityBot no matter how well it is installed. So we collect crawls on your server instead: a Cloudflare Worker, one file of Next.js middleware, or the WordPress plugin. Crawl Coverage then names which method recorded each visit, so you always know what your numbers are built on.",
          mockup: <FeatureScreenshot src="/screenshots/ai-analytics/05-crawl-coverage.png" alt="Crawl Coverage showing bot visits by operator, a provenance line naming the collection method, and how many checkable visits were IP-verified." />,
          reversed: true,
        },
        {
          title: "Split paid AI from organic AI",
          description: "When ChatGPT Ads sends a paid click, the referrer is often stripped, so it lands in your analytics as Direct and disappears. AI Analytics reads OpenAI's own click identifier and separates paid from earned. You see organic AI referrals and paid ChatGPT Ads visits side by side, per assistant and per page, and the paid side carries through to spend, click capture and cost per conversion.",
          mockup: <FeatureScreenshot src="/screenshots/ai-analytics/04-paid-vs-organic.png" alt="AI traffic split into organic AI and paid ChatGPT Ads visit counts, with a referral trend and a per-assistant breakdown." />,
        },
        {
          title: "Find out whether AI traffic actually converts",
          description: "Visits are not the point. The tracking snippet watches for lead-shaped form submissions and attributes each one back to the AI visit that produced it, so you get a conversion rate for AI traffic, the split between organic and paid, and which forms and pages do the converting. Where you are also running ChatGPT Ads, it reconciles the clicks the platform reports against the visits your site actually recorded, so a tracking gap shows up as a tracking gap instead of hiding inside a cost per conversion.",
          mockup: <FeatureScreenshot src="/screenshots/ai-analytics/07-conversions.png" alt="The Conversions tab: AI conversion rate over AI visits and conversions, an organic versus paid ChatGPT Ads attribution split, and tables of the top converting forms and pages." />,
          reversed: true,
        },
        {
          title: "An advisor that reads every source at once",
          description: "Crawl data, search demand, AI referrals, conversions and your AEO score each tell you a fragment. The Analytics Advisor reads them together and writes what changed this week and why, then turns it into two or three things worth doing. You can ask it a follow-up in the same panel. It never touches your ad budgets or bids; those decisions stay yours.",
          mockup: <FeatureScreenshot src="/screenshots/ai-analytics/02-analytics-advisor.png" alt="The Analytics Advisor panel with a written read of the week across crawls, search and AI traffic, suggested focus areas, and a question box." />,
        },
        {
          title: "Install it where it belongs",
          description: "The JavaScript snippet handles the human half: visits referred from AI assistants, which assistant sent them, where they landed, and the forms they submit. Paste it in your head tag or push it through Google Tag Manager. Crawls need a server-side method, and the app tells you which one fits your stack rather than making you guess. Run both and nothing is double counted.",
          mockup: <CodeSnippetMockup />,
        },
      ]}
      platformsCoverage={{
        heading: "Tracks the 6 AI engines your buyers actually use",
        subhead: "Crawler activity and referral traffic from every major AI platform, identified by real user agent.",
        /* Matches the app's AI_REFERRERS + AI bot list (src/lib/analytics/constants.ts,
           2026-10-15): no Grok referral or crawler signature, and AI Overviews
           clicks arrive as ordinary Google traffic, so neither is listed. */
        platforms: [
          { name: "ChatGPT",             icon: "/icons/chatgpt.svg" },
          { name: "Gemini",              icon: "/icons/gemini.svg" },
          { name: "Claude",              icon: "/icons/claude.svg" },
          { name: "Perplexity",          icon: "/icons/perplexity.svg" },
          { name: "Microsoft Copilot",   icon: "/icons/microsoft-copilot.svg" },
          { name: "Meta AI",             icon: "/icons/meta-ai.svg" },
        ],
      }}
      trustSignals={{
        heading: "Built to measure AI honestly, including the parts that are inconvenient",
        items: [
          {
            title: "We say out loud that a snippet cannot see crawlers",
            subtext: "AI crawlers do not execute JavaScript, so every snippet-delivered method is blind to them by construction. The app says so on the install screen and in the empty state, because a silently empty crawl report teaches nobody anything.",
          },
          {
            title: "Crawler identity is checked, and a miss is never called a fake",
            subtext: "A crawl claiming to be GPTBot is checked against OpenAI's published address ranges. A match is marked verified. A non-match is marked unproven, never spoofed, because published lists go stale and operators add addresses before updating them.",
          },
          {
            title: "Four collection methods, and they do not all see the same thing",
            subtext: <>A <a href="/integrations/cloudflare" style={{ color: TEAL, textDecoration: "none", borderBottom: `1px solid ${TEAL}40` }}>Cloudflare Worker</a> also sees cache hits that never reach your origin. <a href="/integrations/nextjs" style={{ color: TEAL, textDecoration: "none", borderBottom: `1px solid ${TEAL}40` }}>Next.js middleware</a> works on any host. The <a href="/integrations/wordpress" style={{ color: TEAL, textDecoration: "none", borderBottom: `1px solid ${TEAL}40` }}>WordPress plugin</a> runs at your origin, so it misses anything a CDN served. Each method is labelled with what it can and cannot observe.</>,
          },
          {
            title: "A companion to Google Analytics, not a replacement",
            subtext: "Site totals and channel sessions come from GA4, which stays the source of truth. AI-Advisors supplies the AI lens on top and reconciles the two, so you are never asked to choose which number to believe.",
          },
          {
            title: "The snippet fires only on AI traffic",
            subtext: "Regular human visitors trigger nothing: no network request, no processing. The script activates only when it detects an AI referrer, an AI ad click identifier, or an AI campaign tag, so there is no measurable effect on page speed.",
          },
        ],
      }}
      relatedPosts={[
        "how-to-track-ai-bot-activity",
        "track-ai-referral-traffic",
        "gptbot-vs-oai-searchbot",
      ]}
      relatedHeading="Read more about AI Analytics"
      connections={[
        { title: "Answer Engine Insights", description: "Turn crawl and traffic movement into the prompts you are winning or losing", href: "/platform/answer-engine-insights" },
        { title: "AEO", description: "Fix the pages AI reads, then watch crawl frequency and AI traffic respond", href: "/platform/aeo" },
        { title: "AI Ads", description: "Measure what ChatGPT Ads spend did to your whole AI presence, not just the ad platform", href: "/platform/ai-ads" },
        { title: "AI Automation", description: "Scheduled scans measure the effect of each AEO fix on AI traffic every week", href: "/platform/ai-automation" },
      ]}
      faqs={[
        { q: "What are AI bot crawlers?", a: "AI bot crawlers are automated programs AI platforms send to read and index your website. GPTBot and OAI-SearchBot work for ChatGPT, ClaudeBot and Claude-SearchBot for Claude, PerplexityBot for Perplexity, and Googlebot for Google, whose standard crawl also feeds Google AI Overviews and AI Mode. Tracking them tells you which AI platforms are actively reading your content, which is the precondition for being cited in their answers." },
        { q: "Why can't a JavaScript snippet track AI crawlers?", a: "Because AI crawlers do not execute JavaScript. A browser snippet only runs after a browser parses your page and runs scripts, and a crawler fetching your HTML never does that. This is a property of how crawlers work, not a limitation you can configure around, and it means any tool promising crawler tracking from a script tag alone cannot deliver it. AI-Advisors collects crawls on the server instead, through a Cloudflare Worker, a Next.js middleware file, or the WordPress plugin." },
        { q: "How do I track AI bots on my website?", a: "Install one of the three server-side methods, where the request's user agent is visible before your page renders. The Cloudflare Worker suits sites already behind Cloudflare and additionally sees requests served from cache. The Next.js middleware is a single file you drop into your project and works on any host. The WordPress plugin is a one-click install. The app detects your stack and recommends the right one. Add the JavaScript snippet as well to capture the human side: visits referred from AI assistants and the forms they submit." },
        { q: "What AI bots does AI Analytics identify?", a: "18 crawler user agents: GPTBot, ChatGPT-User, OAI-SearchBot and OAI-AdsBot from OpenAI; ClaudeBot, Claude-User, Claude-SearchBot and anthropic-ai from Anthropic; Google-Extended and Googlebot; PerplexityBot; Bingbot; FacebookExternalHit and meta-externalagent from Meta; Bytespider; CCBot; cohere-ai; and Applebot-Extended. Each is tracked individually with its own visit count, pages crawled, and last seen time, so you can find the exact crawler behind a spike." },
        { q: "What does IP-verified mean?", a: "A crawl arrives claiming a user agent, and a user agent is trivial to fake. Where an operator publishes the IP address ranges its crawlers use, AI-Advisors checks the request's source address against that list. A match is marked verified. A non-match is marked unproven rather than spoofed, because published ranges go stale and operators routinely add addresses before updating the file. Where an operator publishes nothing, the visit is marked as not checkable rather than counted as a failure." },
        { q: "Does the tracking snippet slow down my website?", a: "No. The snippet only acts when it detects AI traffic, which is a small fraction of visitors. For everyone else it does nothing: no network requests, no processing. The script itself is under 1KB." },
        { q: "Can I use AI Analytics with Google Analytics?", a: "Yes, and it is designed to sit on top of it rather than replace it. GA4 remains the source of truth for site totals and channel sessions; AI-Advisors carves AI arrivals out of Referral and Direct so they can be compared against your other channels without being double counted, and adds the two things GA4 structurally cannot report: AI crawler activity, and paid ChatGPT Ads clicks that arrive with their referrer stripped." },
        { q: "What can AI Analytics tell me that my existing analytics cannot?", a: "Four things. Which AI crawlers read your site, and whether they really were who they claimed to be. How much human traffic AI assistants send you, per assistant and per page. How much of that is paid ChatGPT Ads versus earned, which your analytics records as Direct once the referrer is stripped. And how those move in relation to your search demand, your AEO score, and your conversions, week over week." },
      ]}
      cta={{
        title: "Find out which AI platforms are reading your site",
        subtitle: "Install one file, see the crawlers your snippet cannot, and put AI traffic on the same table as every other channel.",
        primaryLabel: "Start Free Trial",
        primaryHref: "https://app.ai-advisors.ai/signup?utm_source=website&utm_medium=platform&utm_campaign=signup&utm_content=analytics_cta",
      }}
    />
  );
}
